import React, { useState } from 'react';
import { Pen, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { SignaturePad } from './SignaturePad';

/**
 * SignatureField — champ compact combinant :
 *  - Téléversement d'une image (PNG/JPG max 2 Mo)
 *  - OU dessin à la souris / au doigt via <SignaturePad/>
 *
 * value : string base64 (data URL) ou '' — controlled par le parent
 * onChange(base64) : mise à jour du parent
 */
export function SignatureField({ value, onChange, testIdPrefix = 'sig' }) {
  const [mode, setMode] = useState('upload'); // 'upload' | 'draw'

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Fichier image requis (PNG ou JPG)');
    if (file.size > 2 * 1024 * 1024) return toast.error('Image trop volumineuse (max 2 Mo)');
    const reader = new FileReader();
    reader.onload = () => onChange?.(reader.result);
    reader.onerror = () => toast.error('Impossible de lire le fichier');
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 border-b border-gray-200 pb-1">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`text-xs px-3 py-1 rounded-t-md flex items-center gap-1 ${mode === 'upload' ? 'bg-[#B85C38] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid={`${testIdPrefix}-mode-upload`}
        >
          <Upload className="w-3.5 h-3.5" /> Téléverser
        </button>
        <button
          type="button"
          onClick={() => setMode('draw')}
          className={`text-xs px-3 py-1 rounded-t-md flex items-center gap-1 ${mode === 'draw' ? 'bg-[#B85C38] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          data-testid={`${testIdPrefix}-mode-draw`}
        >
          <Pen className="w-3.5 h-3.5" /> Dessiner
        </button>
        <div className="flex-1" />
        {value && (
          <button
            type="button"
            onClick={() => onChange?.('')}
            className="text-[11px] text-red-600 hover:underline flex items-center gap-1"
            data-testid={`${testIdPrefix}-clear`}
          >
            <X className="w-3 h-3" /> Retirer
          </button>
        )}
      </div>

      {mode === 'upload' ? (
        <div>
          <label
            htmlFor={`${testIdPrefix}-file`}
            className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-[#B85C38]/50 rounded-lg px-3 py-2 text-xs text-[#B85C38] hover:bg-[#B85C38]/5"
          >
            <Upload className="w-3.5 h-3.5" />
            {value ? 'Changer la signature' : 'Choisir un fichier PNG / JPG'}
          </label>
          <input
            id={`${testIdPrefix}-file`}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testIdPrefix}-file`}
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Recommandé : PNG avec fond transparent. 2 Mo maximum.
          </p>
        </div>
      ) : (
        <SignaturePad
          width={520}
          height={160}
          onSave={(dataUrl) => { onChange?.(dataUrl); setMode('upload'); toast.success('Signature enregistrée'); }}
        />
      )}

      {value && mode === 'upload' && (
        <div className="rounded-lg border border-[#B85C38]/30 bg-[#F5EFE1]/40 p-2 flex justify-end">
          <img
            src={value}
            alt="Aperçu signature"
            className="max-h-16 object-contain"
            data-testid={`${testIdPrefix}-preview`}
          />
        </div>
      )}
    </div>
  );
}

export default SignatureField;
